import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang, t, tx  } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { Send, CheckCircle, Loader2, ChevronLeft, ChevronRight, Monitor, HardDrive, Cpu, Clock, DollarSign, User } from 'lucide-react';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';


const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const slideIn = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
  transition: { duration: 0.3 },
};

const RESOLUTIONS = ['720p (HD)', '1080p (Full HD)', '1440p (2K)', '4K (Ultra HD)'];
const REFRESH_RATES = ['60 Hz', '75 Hz', '144 Hz', '165 Hz', '240 Hz'];
const DURATIONS_FR = ['Moins d\'1h', '1h – 2h', '2h – 4h', '4h – 7h', '7h et plus'];
const DURATIONS_EN = ['Less than 1h', '1h – 2h', '2h – 4h', '4h – 7h', '7h and more'];
const PRIORITIES_FR = ['Performances', 'Prix', 'Esthétique', 'Délai de livraison'];
const PRIORITIES_EN = ['Performance', 'Price', 'Aesthetics', 'Delivery time'];

const STEP_ICONS = [User, Cpu, HardDrive, Monitor, Clock, DollarSign];

export default function DevisPC() {
  const { lang } = useLang();
  const d = translations.devis_pc;
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState('idle');
  const [form, setForm] = useState({
    // Étape 1
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    // Étape 2
    utilisation: '',
    utilisation_autre: '',
    // Étape 3
    stockage: '',
    // Étape 4
    ecran_conservation: '',
    ecran_modele: '',
    ecran_taille: '',
    ecran_ports: '',
    ecran_supplementaires: '',
    ecran_nombre: '',
    ecran_resolution: '',
    ecran_refresh: '',
    // Étape 5
    duree_utilisation: '',
    // Étape 6
    budget: '',
    date_reception: '',
    priorites: [],
    preferences: '',
  });

  const totalSteps = 6;
  const progress = ((step + 1) / totalSteps) * 100;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const togglePriority = (p) => {
    const current = form.priorites;
    setForm({
      ...form,
      priorites: current.includes(p) ? current.filter(x => x !== p) : [...current, p],
    });
  };

  const handleSubmit = async () => {
    setStatus('loading');
    try {
      const res = await fetch(`/api/devis-pc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lang }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass = "w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none";
  const labelClass = "block text-sm font-medium text-slate-300 mb-2";
  const durations = lang === 'fr' ? DURATIONS_FR : DURATIONS_EN;
  const priorities = lang === 'fr' ? PRIORITIES_FR : PRIORITIES_EN;

  const radioOption = (name, value, label) => (
    <label key={value} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${form[name] === value ? 'border-[#0099D6] bg-[#0099D6]/10 text-white' : 'border-white/10 text-slate-300 hover:border-white/20'}`}>
      <input type="radio" name={name} value={value} checked={form[name] === value} onChange={handleChange} className="sr-only" />
      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${form[name] === value ? 'border-[#0099D6] bg-[#0099D6]' : 'border-slate-500'}`} />
      {label}
    </label>
  );

  const steps = [
    // Étape 1 : Infos personnelles
    <div key={0} className="space-y-5">
      <h2 className="text-2xl font-bold text-white">{t(d.step1_title, lang)}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>{t(d.prenom, lang)} *</label>
          <input type="text" name="prenom" value={form.prenom} onChange={handleChange} required className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t(d.nom, lang)} *</label>
          <input type="text" name="nom" value={form.nom} onChange={handleChange} required className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t(d.email, lang)} *</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t(d.telephone, lang)} *</label>
          <input type="tel" name="telephone" value={form.telephone} onChange={handleChange} required className={inputClass} />
        </div>
      </div>
    </div>,

    // Étape 2 : Utilisation principale
    <div key={1} className="space-y-5">
      <h2 className="text-2xl font-bold text-white">{t(d.step2_title, lang)}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {radioOption('utilisation', 'bureautique', tx(lang, 'Bureautique', 'Office work', { es: 'Automatización de las oficinas', de: 'Büroautomatisierung', it: 'Automazione degli uffici', pt: 'Automatização do escritório' }))}
        {radioOption('utilisation', 'gaming', tx(lang, 'Gamer / Jeux', 'Gaming', { es: 'Gamer / Juegos', de: 'Gamer / Spiele', it: 'Gamer / Giochi', pt: 'Gamer / Jogos' }))}
        {radioOption('utilisation', 'montage', tx(lang, 'Montage Vidéo', 'Video Editing', { es: 'Asamblea de vídeo', de: 'Videomontage', it: 'Montaggio video', pt: 'Montagem de Vídeo' }))}
        {radioOption('utilisation', 'autre', tx(lang, 'Autre', 'Other', { es: 'Otros', de: 'Sonstige', it: 'Altri', pt: 'Outros' }))}
      </div>
      {form.utilisation === 'autre' && (
        <div>
          <label className={labelClass}>{tx(lang, 'Précisez', 'Please specify', { es: 'Especificar', de: 'Angabe', it: 'Specificare', pt: 'Especificar' })}</label>
          <input type="text" name="utilisation_autre" value={form.utilisation_autre} onChange={handleChange} className={inputClass} />
        </div>
      )}
    </div>,

    // Étape 3 : Stockage
    <div key={2} className="space-y-5">
      <h2 className="text-2xl font-bold text-white">{t(d.step3_title, lang)}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {['240 Go', '480 Go', '1 To', '2 To', tx(lang, 'Je ne sais pas', 'Not sure', { es: 'No lo sé', de: 'Ich weiß nicht', it: 'Non lo so', pt: 'Não sei' })].map(v =>
          radioOption('stockage', v, v)
        )}
      </div>
    </div>,

    // Étape 4 : Écrans
    <div key={3} className="space-y-6">
      <h2 className="text-2xl font-bold text-white">{t(d.step4_title, lang)}</h2>
      <div>
        <label className={labelClass}>{t(d.ecran_conservation_label, lang)} *</label>
        <div className="flex gap-3">
          {radioOption('ecran_conservation', 'oui', tx(lang, 'Oui', 'Yes', { es: 'Sí', de: 'Ja', it: 'Sì', pt: 'Sim' }))}
          {radioOption('ecran_conservation', 'non', tx(lang, 'Non', 'No', { es: 'No', de: 'Nein', it: 'No', pt: 'Não' }))}
        </div>
      </div>
      {form.ecran_conservation === 'oui' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 bg-white/5 rounded-xl border border-white/10">
          <div>
            <label className={labelClass}>{tx(lang, 'Modèle', 'Model', { es: 'Modelo', de: 'Modell', it: 'Modello', pt: 'Modelo' })}</label>
            <input type="text" name="ecran_modele" value={form.ecran_modele} onChange={handleChange} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{tx(lang, 'Taille (pouces)', 'Size (inches)', { es: 'Tamaño ( pulgadas)', de: 'Größe (inches)', it: 'Dimensioni (pollici)', pt: 'Tamanho (pontos)' })}</label>
            <input type="text" name="ecran_taille" value={form.ecran_taille} onChange={handleChange} placeholder='ex: 24"' className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{tx(lang, 'Ports disponibles', 'Available ports', { es: 'Puertos disponibles', de: 'Ports verfügbar', it: 'Porte disponibili', pt: 'Portos disponíveis' })}</label>
            <input type="text" name="ecran_ports" value={form.ecran_ports} onChange={handleChange} placeholder="HDMI, DP..." className={inputClass} />
          </div>
        </div>
      )}
      <div>
        <label className={labelClass}>{t(d.ecran_supp_label, lang)}</label>
        <div className="flex gap-3">
          {radioOption('ecran_supplementaires', 'oui', tx(lang, 'Oui', 'Yes', { es: 'Sí', de: 'Ja', it: 'Sì', pt: 'Sim' }))}
          {radioOption('ecran_supplementaires', 'non', tx(lang, 'Non', 'No', { es: 'No', de: 'Nein', it: 'No', pt: 'Não' }))}
        </div>
      </div>
      {form.ecran_supplementaires === 'oui' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 bg-white/5 rounded-xl border border-white/10">
          <div>
            <label className={labelClass}>{tx(lang, 'Nombre d\'écrans', 'Number of screens', { es: 'Número de pantallas', de: 'Anzahl der Bildschirme', it: 'Numero di schermi', pt: 'Número de ecrãs' })}</label>
            <input type="number" name="ecran_nombre" value={form.ecran_nombre} onChange={handleChange} min="1" max="4" className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>{tx(lang, 'Résolution', 'Resolution', { es: 'Resolución', de: 'Entschließung', it: 'Risoluzione', pt: 'Resolução' })}</label>
            <select name="ecran_resolution" value={form.ecran_resolution} onChange={handleChange} className={inputClass}>
              <option value="">{tx(lang, 'Choisir', 'Select', { es: 'Elija', de: 'Wählen', it: 'Scegli', pt: 'Escolher' })}</option>
              {RESOLUTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{tx(lang, 'Taux de rafraîchissement', 'Refresh rate', { es: 'Tasa de reembolso', de: 'Zinssatz', it: 'Tasso di riflessione', pt: 'Taxa de actualização' })}</label>
            <select name="ecran_refresh" value={form.ecran_refresh} onChange={handleChange} className={inputClass}>
              <option value="">{tx(lang, 'Choisir', 'Select', { es: 'Elija', de: 'Wählen', it: 'Scegli', pt: 'Escolher' })}</option>
              {REFRESH_RATES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>,

    // Étape 5 : Utilisation quotidienne
    <div key={4} className="space-y-5">
      <h2 className="text-2xl font-bold text-white">{t(d.step5_title, lang)}</h2>
      <p className="text-slate-400">{t(d.step5_desc, lang)}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {durations.map(v => radioOption('duree_utilisation', v, v))}
      </div>
    </div>,

    // Étape 6 : Finalisation
    <div key={5} className="space-y-5">
      <h2 className="text-2xl font-bold text-white">{t(d.step6_title, lang)}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelClass}>{t(d.budget, lang)}</label>
          <input type="text" name="budget" value={form.budget} onChange={handleChange} placeholder={tx(lang, 'ex: 800€', 'e.g. 800€', { es: 'ex: 800 €', de: 'ex: 800 €', it: 'ex: € 800', pt: 'ex: 800 €' })} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{t(d.date_reception, lang)}</label>
          <input type="date" name="date_reception" value={form.date_reception} onChange={handleChange} className={inputClass} />
        </div>
      </div>
      <div>
        <label className={labelClass}>{t(d.priorites, lang)}</label>
        <div className="flex flex-wrap gap-3 mt-2">
          {priorities.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => togglePriority(p)}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${form.priorites.includes(p) ? 'bg-[#0099D6] border-[#0099D6] text-white' : 'border-white/20 text-slate-300 hover:border-[#0099D6]/50'}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelClass}>{t(d.preferences, lang)}</label>
        <textarea name="preferences" value={form.preferences} onChange={handleChange} rows={4} placeholder={tx(lang, 'Marques préférées, composants souhaités, contraintes particulières...', 'Preferred brands, desired components, special constraints...', { es: 'Marcas favoritas, componentes deseados, restricciones especiales...', de: 'Lieblingsmarken, gewünschte Komponenten, besondere Zwänge...', it: 'Marchi preferiti, componenti desiderati, vincoli speciali...', pt: 'Marcas favoritas, componentes desejados, restrições especiais...' })} className={`${inputClass} resize-none`} />
      </div>
    </div>,
  ];

  if (status === 'success') {
    return (
      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="bg-[#15171E]/80 backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-12 text-center">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-white mb-3">{t(d.success_title, lang)}</h3>
            <p className="text-slate-300">{t(d.success_text, lang)}</p>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <>
      <SEO {...SEO_TEXTES['/devis-pc']} path="/devis-pc" />
    <main data-testid="devis-pc-page">
      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">MÉTÉRIS</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">{t(d.title, lang)}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{t(d.subtitle, lang)}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Progress */}
          <div className="mb-10">
            {/* Step indicators */}
            <div className="flex items-center justify-between mb-4">
              {STEP_ICONS.map((Icon, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${i < step ? 'bg-[#0099D6] border-[#0099D6]' : i === step ? 'border-[#0099D6] bg-[#0099D6]/20' : 'border-white/10 bg-transparent'}`}>
                    <Icon className={`w-4 h-4 ${i <= step ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                  <span className="text-xs text-slate-500 hidden sm:block">{i + 1}</span>
                </div>
              ))}
            </div>
            {/* Bar */}
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0099D6] rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-slate-400 text-sm mt-2 text-right">
              {lang === 'fr' ? `Étape ${step + 1} sur ${totalSteps}` : `Step ${step + 1} of ${totalSteps}`}
            </p>
          </div>

          {/* Step content */}
          <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8 mb-6">
            <AnimatePresence mode="wait">
              <motion.div key={step} {...slideIn}>
                {steps[step]}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(s => s - 1)}
              disabled={step === 0}
              className="flex items-center gap-2 px-5 py-3 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              {tx(lang, 'Précédent', 'Previous', { es: 'Prev', de: 'Vor', it: 'Prec', pt: 'Anterior' })}
            </button>

            {step < totalSteps - 1 ? (
              <button
                type="button"
                onClick={() => setStep(s => s + 1)}
                className="flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold px-6 py-3 rounded-lg transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(0,153,214,0.3)]"
              >
                {tx(lang, 'Suivant', 'Next', { es: 'Siguiente', de: 'Nächste', it: 'Il prossimo', pt: 'Próxima' })}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={status === 'loading'}
                className="flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold px-6 py-3 rounded-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_20px_rgba(0,153,214,0.3)]"
              >
                {status === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                {t(d.submit, lang)}
              </button>
            )}
          </div>
          {status === 'error' && (
            <p className="text-red-400 text-sm mt-3 text-center">{tx(lang, 'Erreur. Veuillez réessayer.', 'Error. Please try again.', { es: 'Error. Por favor, inténtalo de nuevo.', de: 'Fehler. Bitte versuchen Sie es noch mal.', it: 'Errore. Per favore riprovate.', pt: 'Erro. Por favor, tente novamente.' })}</p>
          )}
        </div>
      </section>
    </main>
    </>
  );
}
